import React, {ReactNode} from "react";
import PortalDialogType from "../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../parameter/Parameter";
import PortalFields from "../field/PortalFields";
import Exception from "../../../shared/exception/Exception";
import PortalDocument from "../../document/PortalDocument";
import PortalDialogFields from "../PortalDialogFields";
import DialogDefault from "../parameter/PortalDialogDefault";
import PasswordDialog from "./security/PasswordDialog";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import ErrorCode from "../../../shared/exception/ErrorCode";
import PortalContext from "../../PortalContext";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../shared/dialog/components/AbstractDialog";
import {
	Information,
	Parameter as ParameterType,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import DocumentSettings from "../../document/DocumentSettings";
import {DebugGlobals} from "./settings/DebugDialog";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {initAreaLayerGlobals} from "../../viewer/pdf/AreaLayerInterface";
import {initFormLayerGlobals} from "../../viewer/pdf/form/FormLayerInterface";

export interface PortalDialogProperties<PARAMETER_TYPE extends ParameterType> extends AbstractDialogProperties,
	I18nGlobals, DebugGlobals {
	parameter?: Parameter<PARAMETER_TYPE>;
	context: PortalContext;
}

export interface PortalDialogState<PARAMETER_TYPE extends ParameterType> extends AbstractDialogState {
	parameter?: Parameter<PARAMETER_TYPE>;
	defaultParameter?: Parameter<PARAMETER_TYPE>;
	fields?: PortalFields;
	fieldErrors: Map<any, string>;
	showPasswordDialog: boolean;
	passwordDocument?: PortalDocument;
	showDialog: boolean;
}

export default abstract class PortalDialog<PARAMETER_TYPE extends ParameterType = any,
	PROPERTY_TYPE extends PortalDialogProperties<PARAMETER_TYPE> = any,
	STATUS_TYPE extends PortalDialogState<PARAMETER_TYPE> = any> extends AbstractDialog<PROPERTY_TYPE, STATUS_TYPE> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		let parameter: Parameter<PARAMETER_TYPE> | undefined = this.getDefaultParameter();

		this.state = {
			...this.state,
			fieldErrors: new Map<any, string>(),
			showPasswordDialog: false,
			showDialog: false,
			parameter: parameter,
			defaultParameter: parameter?.clone(),
			fields: PortalDialogFields.fields.get(this.getDialogType())?.getDefaults()
		} as STATUS_TYPE;

		this.hide = this.hide.bind(this);
		this.onShow = this.onShow.bind(this);
		this.onError = this.onError.bind(this);
		this.renderError = this.renderError.bind(this);
		this.renderInfo = this.renderInfo.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PROPERTY_TYPE>, prevState: Readonly<STATUS_TYPE>, snapshot?: any
	): Promise<void> {
		if (this.props.visible && !prevProps.visible) {
			await this.onShow();
		}
	}

	/**
	 * Check if the dialog is enabled
	 */
	public static isEnabled(portalInfo: Information): boolean {
		return true;
	}

	/**
	 * Check if the dialog is active for the given documents
	 */
	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		return this.isEnabled(portalInfo);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		DialogDefault.setParameter(this.getDialogType(), this.state.parameter!, selection);
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.state.fieldErrors.clear();

		this.setState({
			submitted: false,
			showPasswordDialog: false,
			passwordDocument: undefined,
			showDialog: false,
			exception: undefined,
			info: undefined
		}, () => this.props.hide());
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.reset")} disabled={this.state.submitted}
							icon={"icon icon-rotate-270 small"} className="reset-button"
							onClick={() => this.resetParameter()}/>
					<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return errors.size === 0 && !this.state.submitted;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError());
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && this.isValid() && !this.state.showPasswordDialog &&
			!this.state.submitted) {
			await this.submit();
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		let selectedDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let isEditPasswordNeeded: boolean = false;
		let passwordDocument: PortalDocument | undefined;

		if (FunctionHelper.applyValue(this.props.checkPdfPasswords, true)) {
			for (let portalDocument of selectedDocuments) {
				if (this.isEditPasswordNeeded(portalDocument)) {
					isEditPasswordNeeded = true;
					passwordDocument = portalDocument;
					break;
				}
			}
		}

		let parameter: Parameter<PARAMETER_TYPE> | undefined = FunctionHelper.applyValue(
			this.props.parameter, this.getDefaultParameter()
		);

		this.setState({
			parameter: parameter,
			defaultParameter: parameter?.clone(),
			fields: PortalDialogFields.fields.get(this.getDialogType())?.get(
				this.props.context.documentManager.getSelection()
			),
			showPasswordDialog: isEditPasswordNeeded,
			passwordDocument: passwordDocument,
			showDialog: true
		});
	};

	/**
	 * Handles a dialog exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.PDF_PASSWORD_OWNER:
				this.setState({
					submitted: false,
					showPasswordDialog: true,
					passwordDocument: undefined,
					exception: portalException
				});
				break;
			default:
				return super.onError(exception);
		}

		return false;
	}

	protected resetMouseMode(documentSelection: Array<PortalDocument>): void {
		for (let selected of documentSelection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.resetMouseMode();
		}

		this.props.context.documentManager.updateGlobalDocumentList();

		this.props.context.getPage().setState({
			...initAreaLayerGlobals(),
			...initFormLayerGlobals()
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.showPasswordDialog) {
			return <PasswordDialog {...this.props} visible={this.state.showPasswordDialog}
								   portalDocument={FunctionHelper.applyValue(
									   this.state.passwordDocument,
									   this.props.context.documentManager.getSelection()[0]
								   )}
								   close={this.hide} exception={this.state.exception}
								   hide={() => this.setState({
									   exception: undefined,
									   info: undefined,
								   }, () => this.onShow())}/>;
		}

		if (this.state.showDialog) {
			return super.render();
		}

		return null;
	}

	/**
	 * render component as react node
	 */
	abstract renderDialog(): ReactNode;

	/**
	 * Returns the dialog type
	 */
	protected abstract getDialogType(): PortalDialogType;

	/**
	 * Returns the dialog parameters
	 */
	protected abstract getDefaultParameter(): Parameter<PARAMETER_TYPE> | undefined;

	protected async resetParameter(): Promise<void> {
		this.state.fieldErrors.clear();

		this.setState({
			parameter: this.state.defaultParameter?.clone()
		});
	}
}